<script lang="ts">
import { isKeyboardNavigationFocus } from './focusOrigin'

/**
 * The close function of the one tooltip that is open right now, or null.
 *
 * Only one tooltip should ever be on screen. reka-ui tries to enforce this
 * itself: an opening tooltip dispatches a `tooltip.open` event that every
 * other open tooltip listens for. But reka dispatches that event on
 * `document`, without bubbling, and listens for it on `window`. So the event
 * never arrives, and a tooltip that missed its own close stays on screen
 * next to the new one.
 *
 * This holder does the same job. It lives at module level so that every
 * Tooltip in the app shares it.
 */
let closeCurrentlyOpenTooltip: (() => void) | null = null

/**
 * True while a focus event that the user did not cause is being dispatched.
 *
 * reka opens a tooltip from inside the trigger's `focus` handler. With
 * `ignore-non-keyboard-focus` it already skips focus that came from a mouse
 * click. But focus handed back by a closing menu still counts as keyboard
 * focus when the menu was closed with a key, and so does focus restored when
 * the window regains focus. This flag is set by a capture listener, which runs
 * before reka's handler, and cleared as soon as the event is over. An open
 * requested while it is set is refused. See `focusOrigin.ts`.
 */
let refuseOpenFromThisFocus = false

if (typeof document !== 'undefined') {
  document.addEventListener(
    'focus',
    (event) => {
      refuseOpenFromThisFocus = !isKeyboardNavigationFocus(event as FocusEvent)
      // A timer, not a microtask. For focus the browser dispatches itself, such
      // as the window regaining focus, microtasks run between one listener and
      // the next, so a microtask would clear the flag before reka's handler ran.
      setTimeout(() => {
        refuseOpenFromThisFocus = false
      }, 0)
    },
    true,
  )
}
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { TooltipProvider, TooltipRoot, type TooltipRootEmits, type TooltipRootProps, useForwardProps } from 'reka-ui'
import { TOOLTIP_OPEN_DELAY_MS, TOOLTIP_SKIP_DELAY_MS } from './constants'

const props = withDefaults(defineProps<TooltipRootProps>(), {
  // Without an explicit undefined, Vue turns a missing boolean prop into
  // false. A missing `open` would then look like a tooltip held shut.
  open: undefined,
  defaultOpen: false,
})
const emits = defineEmits<TooltipRootEmits>()

const forwardedProps = useForwardProps(reactiveOmit(props, 'open', 'defaultOpen'))

/**
 * The open state of a tooltip whose parent does not pass `open`. A parent
 * that passes `open` owns the state, and this value is ignored.
 */
const uncontrolledOpen = ref(props.defaultOpen)
const isOpen = computed(() => props.open ?? uncontrolledOpen.value)

function setOpen(nextOpen: boolean): void {
  if (nextOpen && refuseOpenFromThisFocus) {
    return
  }
  uncontrolledOpen.value = nextOpen
  emits('update:open', nextOpen)
}

function closeThisTooltip(): void {
  setOpen(false)
}

watch(
  isOpen,
  (nowOpen) => {
    if (nowOpen) {
      if (closeCurrentlyOpenTooltip && closeCurrentlyOpenTooltip !== closeThisTooltip) {
        closeCurrentlyOpenTooltip()
      }
      closeCurrentlyOpenTooltip = closeThisTooltip
    } else if (closeCurrentlyOpenTooltip === closeThisTooltip) {
      closeCurrentlyOpenTooltip = null
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (closeCurrentlyOpenTooltip === closeThisTooltip) {
    closeCurrentlyOpenTooltip = null
  }
})
</script>

<template>
  <!--
    Each Tooltip carries its OWN provider so the open-delay is isolated per tooltip.
    Under a single shared provider, Reka "skips" the delay for adjacent tooltips: once any
    tooltip is open its `isOpenDelayed` flag is false, so hovering a different element opens
    its tooltip instantly. A per-tooltip provider keeps each tooltip's delay self-contained,
    so moving from one element to another still waits the full delay. Default comes from the
    shared TOOLTIP_OPEN_DELAY_MS; override per instance with `:delay-duration` (e.g. 0 for
    immediate) on <Tooltip>.

    `ignore-non-keyboard-focus`: focus opens a tooltip only when the focus came
    from the keyboard. Without it, reka opens a tooltip on any focus. Closing a
    menu or a dialog hands focus back to the button that opened it, and so does
    returning to the browser window. Each time, that button's tooltip opened
    with the pointer nowhere near it, and stayed open.
  -->
  <TooltipProvider
    :delay-duration="props.delayDuration ?? TOOLTIP_OPEN_DELAY_MS"
    :skip-delay-duration="TOOLTIP_SKIP_DELAY_MS"
    :disable-hoverable-content="true"
    :ignore-non-keyboard-focus="true"
  >
    <TooltipRoot
      data-slot="tooltip"
      v-bind="forwardedProps"
      :open="isOpen"
      @update:open="setOpen"
    >
      <slot :open="isOpen" />
    </TooltipRoot>
  </TooltipProvider>
</template>
